import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import './Carousel.scss';

/**
 * @param {number} number
 * @returns {string}
 */
const pad = (number) => ('0' + number).slice(-2);

/**
 * Carrossel infinito e centralizado: o item ativo fica no meio e metade dos
 * vizinhos aparece cortada nas laterais. A rolagem é nativa (scroll-snap), então
 * o arraste no mobile funciona sem nenhuma lib.
 *
 * O "infinito" vem de três cópias da lista: navega-se sempre pela cópia do meio
 * e, quando a rolagem termina fora dela, o scroll salta uma cópia inteira sem
 * animação. Como as cópias são idênticas, o salto é invisível.
 */
const Carousel = ({ items }) => {
  /** @var object */
  const trackRef = useRef(null);

  /** @var number */
  const total = items.length;

  /** @var array */
  const slides = items.concat(items, items);

  /** Índice na lista triplicada — começa na primeira foto da cópia do meio. */
  const [activeIndex, setActiveIndex] = useState(total);

  /**
   * Espelha o `activeIndex` para que dois cliques seguidos nas setas andem dois
   * slides, sem depender do estado de um render que ainda não aconteceu.
   *
   * @var object
   */
  const activeIndexRef = useRef(total);

  /**
   * Distância entre o centro de um slide e o centro da trilha. É medida pelos
   * centros porque os slides inativos estão reduzidos por `transform: scale`.
   *
   * @var function
   */
  const offsetFromCenter = useCallback((slide) => {
    const trackRect = trackRef.current.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();

    return (
      slideRect.left + slideRect.width / 2 - (trackRect.left + trackRect.width / 2)
    );
  }, []);

  /**
   * Largura de uma cópia inteira da lista, em pixels.
   *
   * @var function
   */
  const copyWidth = useCallback(() => {
    const track = trackRef.current;

    return (
      offsetFromCenter(track.children[total]) - offsetFromCenter(track.children[0])
    );
  }, [offsetFromCenter, total]);

  /** @var function */
  const setActive = useCallback((index) => {
    activeIndexRef.current = index;
    setActiveIndex(index);
  }, []);

  /** @var function */
  const goTo = useCallback(
    (index) => {
      const track = trackRef.current;
      const target = Math.max(0, Math.min(index, total * 3 - 1));
      const slide = track.children[target];

      if (!slide) return;

      setActive(target);

      // posição absoluta: cliques em sequência não se anulam no meio da animação
      track.scrollTo({
        left: track.scrollLeft + offsetFromCenter(slide),
        behavior: 'smooth',
      });
    },
    [offsetFromCenter, setActive, total]
  );

  // abre já centralizado na cópia do meio, antes da primeira pintura
  useLayoutEffect(() => {
    trackRef.current.scrollLeft = copyWidth();
  }, [copyWidth]);

  // quando a rolagem para: atualiza o indicador e volta para a cópia do meio
  useEffect(() => {
    const track = trackRef.current;
    let timer = null;

    const sync = () => {
      let nearest = 0;
      let smallest = Infinity;

      for (let i = 0; i < track.children.length; i++) {
        const distance = Math.abs(offsetFromCenter(track.children[i]));

        if (distance < smallest) {
          smallest = distance;
          nearest = i;
        }
      }

      const centered = total + (nearest % total);
      const copies = (centered - nearest) / total;

      if (copies !== 0) {
        track.scrollLeft += copies * copyWidth();
      }

      setActive(centered);
    };

    const onScroll = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(sync, 130);
    };

    track.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onScroll);

    return () => {
      track.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      window.clearTimeout(timer);
    };
  }, [copyWidth, offsetFromCenter, setActive, total]);

  // render
  return (
    <div className="carousel">
      <div
        className="carousel__track"
        ref={trackRef}>
        {slides.map((item, key) => (
          <div
            key={key}
            className={`carousel__slide ${key === activeIndex ? 'is-active' : ''}`}>
            <picture>
              {item.avif &&
                <source srcset={item.avif} type="image/avif" />
              }
              {item.webp &&
                <source srcset={item.webp} type="image/webp" />
              }
              <img
                loading="lazy"
                src={item.src}
                alt={item.alt}
              />
            </picture>
          </div>
        ))}
      </div>

      <div className="carousel__controls">
        <button
          type="button"
          className="carousel__control carousel__control--prev"
          aria-label="Imagem anterior"
          onClick={() => goTo(activeIndexRef.current - 1)}>
        </button>

        <div className="carousel__counter">
          <span className="current">{pad((activeIndex % total) + 1)}</span>
          <span className="total">/{pad(total)}</span>
        </div>

        <button
          type="button"
          className="carousel__control carousel__control--next"
          aria-label="Próxima imagem"
          onClick={() => goTo(activeIndexRef.current + 1)}>
        </button>
      </div>
    </div>
  );
}

export default Carousel;
